// Guest profile inference, moved verbatim from the pre-redesign SubmitPage (IMPL_ADDENDUM §1.1):
// the profile is auto-detected from the file extension, never chosen.
//   inferProfile(file, mode) → { profile, reason }                 ".exe · Windows binary", "URL · browser detonation", …
//   routeProfile(inferred, available) → { profile, note, routed }  falls back to a profile the fleet offers:
//       "… · no node offers linux-test, routing to windows"; available = profiles of the listed nodes

export type Mode = "file" | "url";

// File → profile mapping. Windows gets the long tail: PE formats, scripts,
// Office/PDF, archives/images that mount, LNK/HTA/CPL. Linux gets the
// native shapes. URL submissions default to the browser profile (windows).
export const WINDOWS_EXTS = new Set([
  "exe","dll","sys","com","scr","cpl","ocx","msi","msix","appx",
  "bat","cmd","ps1","psm1","psd1","vbs","vbe","js","jse","wsf","wsh","hta","lnk",
  "doc","docx","docm","xls","xlsx","xlsm","xlsb","ppt","pptx","pptm","rtf","one","pub",
  "pdf","xps",
  "jar",
  "iso","img","vhd","vhdx","udf","wim",
  "msg","eml",
]);
export const LINUX_EXTS = new Set([
  "elf","sh","bash","so","deb","rpm","apk","py","pl",
]);

// The two Windows SKUs the fleet offers today. Order matters: the first
// is the default when the user hasn't picked one explicitly.
export const WINDOWS_PROFILES = ["windows-11-23h2", "windows-10-22h2"] as const;
export type WindowsProfile = typeof WINDOWS_PROFILES[number];
export function isWindowsProfile(p: string): p is WindowsProfile {
  return (WINDOWS_PROFILES as readonly string[]).includes(p);
}
const DEFAULT_WINDOWS: WindowsProfile = "windows-11-23h2";

export function inferProfile(file: File | null, mode: Mode): { profile: string; reason: string } {
  if (mode === "url") return { profile: DEFAULT_WINDOWS, reason: "URL · browser detonation" };
  if (!file) return { profile: DEFAULT_WINDOWS, reason: "default" };
  const name = file.name.toLowerCase();
  const dot = name.lastIndexOf(".");
  const ext = dot >= 0 ? name.slice(dot + 1) : "";
  if (LINUX_EXTS.has(ext)) return { profile: "linux-test", reason: `.${ext} · ELF/script` };
  if (WINDOWS_EXTS.has(ext)) return { profile: DEFAULT_WINDOWS, reason: `.${ext} · Windows binary` };
  return { profile: DEFAULT_WINDOWS, reason: "unknown ext · default to Windows" };
}

// If the inferred profile isn't offered by any live node, fall back to
// whatever the fleet does offer so the submit still routes somewhere.
export function routeProfile(
  inferred: { profile: string; reason: string },
  available: ReadonlySet<string>,
): { profile: string; note: string; routed: boolean } {
  const profile =
    available.has(inferred.profile) || available.size === 0
      ? inferred.profile
      : Array.from(available)[0];
  const profileNote =
    profile === inferred.profile
      ? inferred.reason
      : `${inferred.reason} · no node offers ${inferred.profile}, routing to ${profile}`;
  return { profile, note: profileNote, routed: profile !== inferred.profile };
}
